/**
 * WebSocket client following ws_manager.go pattern
 * Handles connection management, message handlers, and reconnection logic
 */

import { ConnectionState, MessageHandler } from '../../types/transport';
import { WebSocketClientConfig } from '../../types/client';

// Re-export for backward compatibility
export { ConnectionState } from '../../types/transport';
export type { MessageHandler } from '../../types/transport';
export type { WebSocketClientConfig } from '../../types/client';

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private state: ConnectionState = ConnectionState.Disconnected;
  private handlers: Map<string, MessageHandler[]> = new Map();
  private reconnectTimer: number | null = null;
  private reconnectAttempts: number = 0;
  private config: Required<Omit<WebSocketClientConfig, 'url'>> & { url: string };
  private originalConfig: WebSocketClientConfig; // Store original to check if values were explicitly provided
  private pingInterval: number | null = null;
  private stateChangeHandlers: Set<(state: ConnectionState) => void> = new Set();
  
  // Authentication state tracking
  private authState: 'pending' | 'authenticated' | 'failed' | 'not_required' = 'not_required';
  
  // Subscription acknowledgment tracking
  private pendingSubscriptions: Map<string, { resolve: () => void; reject: (error: Error) => void; timeout: number }> = new Map();

  constructor(config: WebSocketClientConfig) {
    this.originalConfig = config; // Store original config
    // Convert HTTP/HTTPS URL to WebSocket URL if needed
    const wsUrl = this.convertHTTPToWebSocketURL(config.url);
    this.config = {
      url: wsUrl,
      authToken: config.authToken || '',
      walletAddress: config.walletAddress || '',
      deviceID: config.deviceID || '',
      reconnectInterval: config.reconnectInterval || 5000,
      maxReconnectAttempts: config.maxReconnectAttempts || 10,
    };
  }

  /**
   * Convert HTTP/HTTPS URL to WebSocket URL
   * Mirrors convertHTTPToWebSocketURL from ws_manager.go
   * Ensures router.morpheum.io/websocket is used as the endpoint
   */
  private convertHTTPToWebSocketURL(apiBaseURL: string): string {
    // If already a WebSocket URL, return as-is (but ensure it's router.morpheum.io/websocket if applicable)
    if (apiBaseURL.startsWith('ws://') || apiBaseURL.startsWith('wss://')) {
      // If it's router.morpheum.io, ensure it has /websocket path
      if (apiBaseURL.includes('router.morpheum.io')) {
        const url = new URL(apiBaseURL);
        if (!url.pathname || url.pathname === '/') {
          url.pathname = '/websocket';
          return url.toString();
        }
      }
      return apiBaseURL;
    }

    // Handle localhost/127.0.0.1 (for development)
    if (apiBaseURL.includes('localhost') || apiBaseURL.includes('127.0.0.1')) {
      // Extract port if present
      const portMatch = apiBaseURL.match(/:(\d+)/);
      const port = portMatch ? portMatch[1] : '8080';
      return `ws://localhost:${port}/websocket`;
    }

    // Convert HTTP to WS
    if (apiBaseURL.startsWith('http://')) {
      const url = apiBaseURL.replace('http://', 'ws://');
      // Ensure /websocket path is appended
      if (!url.includes('/websocket')) {
        return url.endsWith('/') ? `${url}websocket` : `${url}/websocket`;
      }
      return url;
    }

    // Convert HTTPS to WSS
    if (apiBaseURL.startsWith('https://')) {
      const url = apiBaseURL.replace('https://', 'wss://');
      // Ensure /websocket path is appended
      if (!url.includes('/websocket')) {
        return url.endsWith('/') ? `${url}websocket` : `${url}/websocket`;
      }
      return url;
    }

    // Default: use router.morpheum.io/websocket
    // This ensures the endpoint is always router.morpheum.io/websocket when no URL is provided
    if (!apiBaseURL || apiBaseURL.trim() === '') {
      return 'wss://router.morpheum.io/websocket';
    }

    // If it's just a domain name, assume HTTPS and convert
    if (apiBaseURL.includes('router.morpheum.io')) {
      return 'wss://router.morpheum.io/websocket';
    }

    // Fallback: try to construct WebSocket URL
    try {
      const url = new URL(apiBaseURL.startsWith('http') ? apiBaseURL : `https://${apiBaseURL}`);
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      const path = url.pathname && url.pathname !== '/' ? url.pathname : '';
      const wsPath = path.endsWith('/websocket') ? path : `${path}/websocket`;
      return `${wsProtocol}//${url.host}${wsPath}`;
    } catch (e) {
      // Final fallback: use router.morpheum.io/websocket
      console.warn(`Failed to parse URL ${apiBaseURL}, defaulting to router.morpheum.io/websocket`);
      return 'wss://router.morpheum.io/websocket';
    }
  }

  /**
   * Get connection URL with authentication parameters
   * Follows getConnectionURL pattern from ws_manager.go
   * Note: Includes wallet parameter if explicitly provided (even if empty)
   */
  private getConnectionURL(): string {
    const params: string[] = [];
    
    // Add token parameter if provided (not required currently)
    if (this.config.authToken) {
      params.push(`token=${encodeURIComponent(this.config.authToken)}`);
    }
    
    // Add wallet address parameter if explicitly provided in original config
    // (even if empty string, as server may require the parameter to be present)
    if ('walletAddress' in this.originalConfig && this.originalConfig.walletAddress !== undefined) {
      params.push(`wallet=${encodeURIComponent(this.originalConfig.walletAddress || '')}`);
    }
    
    // Add device ID parameter if provided
    if (this.config.deviceID) {
      params.push(`device_id=${encodeURIComponent(this.config.deviceID)}`);
    }

    const separator = this.config.url.includes('?') ? '&' : '?';
    return params.length > 0 
      ? `${this.config.url}${separator}${params.join('&')}`
      : this.config.url;
  }

  /**
   * Connect to WebSocket server
   * Follows Connect pattern from ws_manager.go
   */
  async connect(): Promise<void> {
    if (this.state === ConnectionState.Connected || 
        this.state === ConnectionState.Connecting) {
      return;
    }

    this.setState(ConnectionState.Connecting);

    return new Promise((resolve, reject) => {
      let connectionOpened = false;
      let rejectCalled = false;
      let connectionTimeout: number | null = null;

      const handleReject = (error: Error) => {
        if (!rejectCalled) {
          rejectCalled = true;
          if (connectionTimeout !== null) {
            clearTimeout(connectionTimeout);
            connectionTimeout = null;
          }
          this.setState(ConnectionState.Disconnected);
          reject(error);
        }
      };

      try {
        const url = this.getConnectionURL();
        console.log('Connecting to WebSocket:', url);
        const ws = new WebSocket(url);

        // Set a connection timeout (10 seconds)
        connectionTimeout = window.setTimeout(() => {
          if (!connectionOpened && !rejectCalled) {
            ws.close();
            handleReject(new Error(`WebSocket connection timeout after 10 seconds. Unable to connect to ${url}. Check if the server is running and accessible.`));
          }
        }, 10000);

        ws.onopen = async () => {
          if (connectionTimeout !== null) {
            clearTimeout(connectionTimeout);
            connectionTimeout = null;
          }
          connectionOpened = true;
          this.ws = ws;
          this.setState(ConnectionState.Connected);
          this.reconnectAttempts = 0;
          
          // Authentication is handled via URL parameters (token in connection URL)
          // No auth message needs to be sent - server authenticates during connection handshake
          if (this.config.authToken) {
            this.authState = 'authenticated';
            console.log('[WebSocketClient] Authentication via URL token');
          } else {
            this.authState = 'not_required';
          }
          
          this.startPing();
          resolve();
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        ws.onerror = (event: Event) => {
          console.error('WebSocket error event:', event);
          // Don't reject here - let onclose provide detailed error with close code
          // The onclose handler will provide more detailed error information
        };

        ws.onclose = (event: CloseEvent) => {
          if (connectionTimeout !== null) {
            clearTimeout(connectionTimeout);
            connectionTimeout = null;
          }
          this.ws = null;
          this.stopPing();
          
          // Reset auth state on disconnect
          this.authState = 'not_required';

          // Only reject if connection never opened (failed connection)
          if (!connectionOpened && !rejectCalled) {
            const closeCode = event.code;
            const closeReason = event.reason || 'Unknown reason';
            const wasClean = event.wasClean;
            let errorMessage = `WebSocket connection failed. Close code: ${closeCode}`;
            
            // Add common close code meanings
            if (closeCode === 1006) {
              errorMessage = `WebSocket connection failed (abnormal closure, code ${closeCode}). Unable to connect to ${url}. Possible causes: Server is not running, network issue, firewall blocking connection, or incorrect URL.`;
            } else if (closeCode === 1002) {
              errorMessage = `WebSocket protocol error (code ${closeCode}). The server may not support WebSocket protocol or there's a protocol mismatch.`;
            } else if (closeCode === 1003) {
              errorMessage = `WebSocket unsupported data type (code ${closeCode}).`;
            } else if (closeCode === 1008) {
              errorMessage = `WebSocket connection closed due to policy violation (code ${closeCode}): ${closeReason}`;
            } else if (closeCode === 1011) {
              errorMessage = `WebSocket server error (code ${closeCode}): ${closeReason}`;
            } else if (closeCode === 1000) {
              errorMessage = `WebSocket connection closed normally (code ${closeCode}) but before opening.`;
            } else if (closeReason && closeReason !== 'Unknown reason') {
              errorMessage += ` Reason: ${closeReason}`;
            }
            
            if (!wasClean && closeCode === 1006) {
              errorMessage += ` This usually indicates the connection was refused or the server is unreachable.`;
            }
            
            handleReject(new Error(errorMessage));
          } else if (connectionOpened) {
            // Connection was opened, this is a normal close
            this.setState(ConnectionState.Disconnected);
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        if (connectionTimeout !== null) {
          clearTimeout(connectionTimeout);
          connectionTimeout = null;
        }
        const errorMessage = error instanceof Error ? error.message : String(error);
        handleReject(new Error(`Failed to create WebSocket connection: ${errorMessage}`));
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.cancelReconnect();
    this.stopPing();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    // Clear all handlers
    this.handlers.clear();
    this.stateChangeHandlers.clear();
    
    this.setState(ConnectionState.Disconnected);
  }

  /**
   * Send message through WebSocket
   */
  send(data: Record<string, any>): void {
    if (this.state !== ConnectionState.Connected || !this.ws) {
      throw new Error('WebSocket not connected');
    }

    // Verify WebSocket is actually in OPEN state
    if (this.ws.readyState !== WebSocket.OPEN) {
      const stateName = this.ws.readyState === WebSocket.CONNECTING ? 'CONNECTING' :
                       this.ws.readyState === WebSocket.CLOSING ? 'CLOSING' :
                       this.ws.readyState === WebSocket.CLOSED ? 'CLOSED' : 'UNKNOWN';
      console.warn(`[WebSocketClient] WebSocket not in OPEN state (${stateName}), updating connection state`);
      this.setState(ConnectionState.Disconnected);
      throw new Error(`WebSocket not open (state: ${stateName})`);
    }

    try {
      // Ensure params is always an object if present
      if (data.params && typeof data.params !== 'object') {
        console.warn('[WebSocketClient] params must be an object, converting:', data.params);
        data.params = {};
      }
      
      const message = JSON.stringify(data);
      this.ws.send(message);
    } catch (error) {
      console.error('[WebSocketClient] Failed to send message:', error);
      // Check if connection is still open
      if (this.ws?.readyState !== WebSocket.OPEN) {
        console.warn('[WebSocketClient] WebSocket not open after send error, scheduling reconnect');
        this.setState(ConnectionState.Disconnected);
        this.scheduleReconnect();
      }
      throw error;
    }
  }

  /**
   * Register message handler for a specific message type
   * Follows On pattern from ws_manager.go
   */
  on(messageType: string, handler: MessageHandler): void {
    if (!this.handlers.has(messageType)) {
      this.handlers.set(messageType, []);
    }
    this.handlers.get(messageType)!.push(handler);
  }

  /**
   * Unregister message handler
   */
  off(messageType: string, handler?: MessageHandler): void {
    if (!handler) {
      // Remove all handlers for this message type
      this.handlers.delete(messageType);
      return;
    }

    const handlers = this.handlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Check if connected
   * Verifies both the internal state and the actual WebSocket readyState
   */
  isConnected(): boolean {
    const stateConnected = this.state === ConnectionState.Connected;
    const wsOpen = this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    
    // If state says connected but WebSocket is not open, update state
    if (stateConnected && !wsOpen) {
      console.warn('[WebSocketClient] State is Connected but WebSocket is not OPEN, updating state');
      this.setState(ConnectionState.Disconnected);
      return false;
    }
    
    return stateConnected && wsOpen;
  }

  /**
   * Set connection state and notify handlers
   */
  private setState(state: ConnectionState): void {
    const previousState = this.state;
    this.state = state;
    
    // Notify state change handlers if state actually changed
    if (previousState !== state) {
      this.stateChangeHandlers.forEach(handler => {
        try {
          handler(state);
        } catch (error) {
          console.error('Error in state change handler:', error);
        }
      });
    }
  }

  /**
   * Register a callback for connection state changes
   * Returns an unsubscribe function
   */
  onStateChange(handler: (state: ConnectionState) => void): () => void {
    this.stateChangeHandlers.add(handler);
    return () => {
      this.stateChangeHandlers.delete(handler);
    };
  }

  /**
   * Handle incoming message
   * Updated to support action-based protocol (primary) with backward compatibility for Router protocol
   */
  private handleMessage(data: Record<string, any>): void {
    // Handle ping messages from server - respond with pong
    // Per API spec: server may send { "type": "ping" } or { "type": "ping", "id": "ping_123" }
    if (data.type === 'ping') {
      try {
        // Verify connection is still open before responding
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          // Respond with pong, echoing the ID if provided
          // Format: { "type": "pong" } or { "type": "pong", "id": "ping_123" } per API spec
          const pongMessage: Record<string, any> = { type: 'pong' };
          if (data.id) {
            pongMessage.id = data.id;
          }
          this.send(pongMessage);
        }
      } catch (error) {
        console.error('[WebSocketClient] Failed to send pong:', error);
        // If pong fails, connection might be dead
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          this.setState(ConnectionState.Disconnected);
          this.scheduleReconnect();
        }
      }
      // Don't route ping to handlers
      return;
    }
    
  
    // Handle subscription acknowledgments
    // Router protocol: type="ack"
    if (data.type === 'ack' && typeof data.channel === 'string') {
      const pending = this.pendingSubscriptions.get(data.channel);
      if (pending) {
        clearTimeout(pending.timeout);
        pending.resolve();
        this.pendingSubscriptions.delete(data.channel);
      }
    }
    // Action-based protocol: action="subscribed"
    if (data.action === 'subscribed' && typeof data.channel === 'string') {
      const pending = this.pendingSubscriptions.get(data.channel);
      if (pending) {
        clearTimeout(pending.timeout);
        pending.resolve();
        this.pendingSubscriptions.delete(data.channel);
      }
    }
    
    // Check for stale data flag
    if (data.is_stale === true) {
      console.warn(`[WebSocketClient] Received stale data:`, data);
      // Emit stale event if handlers are registered
      const staleHandlers = this.handlers.get('stale') || [];
      staleHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('[WebSocketClient] Error in stale data handler:', error);
        }
      });
    }
    
    // Determine message routing key
    // Action-based protocol uses "action" (e.g., action="subscribed", action="error")
    // Router protocol uses "type" + "channel" (e.g., type="data", channel="last_price") - backward compatibility
    // Old protocol uses "event" (e.g., event="orderbook_update") - backward compatibility
    let routingKey: string;

    if (typeof data.action === 'string') {
      // Action-based protocol format (primary)
      if (data.action === 'subscribed') {
        // Subscription acknowledgment - route by channel if present
        if (typeof data.channel === 'string') {
          routingKey = data.channel;
        } else {
          routingKey = 'subscribed';
        }
      } else if (data.action === 'error') {
        // Error message
        routingKey = 'error';
        // Log error details
        if (data.error) {
          console.error('[WebSocketClient] Server error:', {
            code: data.error.code,
            message: data.error.message,
            details: data.error.details,
          });
        } else if (data.message) {
          console.error('[WebSocketClient] Server error:', data.message);
        }
      } else if (data.action === 'pong') {
        // Pong message (legacy action-based format - kept for backward compatibility)
        routingKey = 'pong';
      } else if (data.action === 'data' || data.action === 'update') {
        // Data/update message - route by channel
        if (typeof data.channel === 'string') {
          routingKey = data.channel;
        } else {
          console.error('[WebSocketClient] Missing channel in data message');
          return;
        }
      } else {
        // Other action messages
        routingKey = data.action;
      }
    } else if (typeof data.type === 'string') {
      // Router protocol format (primary for Router API)
      if (data.type === 'data') {
        // Data message - route by channel
        if (typeof data.channel === 'string') {
          routingKey = data.channel;
        } else {
          console.error('[WebSocketClient] Missing channel in data message');
          return;
        }
      } else if (data.type === 'error') {
        // Error message
        routingKey = 'error';
        // Log error details
        if (data.error) {
          console.error('[WebSocketClient] Server error:', {
            code: data.error.code,
            message: data.error.message,
            details: data.error.details,
          });
        } else if (data.message) {
          console.error('[WebSocketClient] Server error:', data.message);
        }
      } else if (data.type === 'ack') {
        // Acknowledgment message - route by channel if present
        if (typeof data.channel === 'string') {
          routingKey = data.channel;
        } else {
          routingKey = 'ack';
        }
      } else if (data.type === 'pong') {
        // Pong message
        routingKey = 'pong';
      } else {
        // Other type messages (e.g., "ping")
        routingKey = data.type;
      }
    } else if (typeof data.event === 'string') {
      // Old protocol format (backward compatibility)
      routingKey = data.event;
    } else if (typeof data.channel === 'string') {
      // Direct channel message (fallback)
      routingKey = data.channel;
    } else {
      // Fallback to 'message' if no action, type, event, or channel
      routingKey = 'message';
    }

    // Route to handlers
    const handlers = this.handlers.get(routingKey) || [];
    const allHandlers = this.handlers.get('*') || [];

    // Call specific handlers for the routing key
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[WebSocketClient] Error in handler for ${routingKey}:`, error);
      }
    });

    // Call wildcard handlers (but skip if already handled as error)
    if (routingKey !== 'error') {
      allHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('[WebSocketClient] Error in wildcard handler:', error);
        }
      });
    }
  }

  /**
   * Schedule reconnection attempt
   * Follows reconnection pattern from ws_manager.go
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error('[WebSocketClient] Max reconnection attempts reached');
      this.setState(ConnectionState.Disconnected);
      return;
    }

    this.reconnectAttempts++;
    this.setState(ConnectionState.Reconnecting);

    this.reconnectTimer = window.setTimeout(() => {
      this.connect().catch(error => {
        console.error('[WebSocketClient] Reconnection failed:', error);
      });
    }, this.config.reconnectInterval);
  }

  /**
   * Cancel scheduled reconnection
   */
  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
  }

  /**
   * Start ping interval to keep connection alive
   * Uses 20 second interval to stay well below common 30-second proxy/load balancer timeouts
   * Sends application-level ping messages as per WebSocket Ping/Pong API spec
   */
  private startPing(): void {
    // Clear any existing ping interval
    this.stopPing();
    
    this.pingInterval = window.setInterval(() => {
      if (this.ws && this.state === ConnectionState.Connected) {
        // Check if WebSocket is actually still open
        if (this.ws.readyState === WebSocket.OPEN) {
          try {
            // Send application-level ping message with optional ID for tracking
            // Format: { "type": "ping", "id": "ping_123" } per API spec
            const pingId = `ping_${Date.now()}`;
            this.send({ type: 'ping', id: pingId });
          } catch (error) {
            console.error('[WebSocketClient] Failed to send ping:', error);
            // If ping fails, the connection might be dead - trigger reconnection
            if (this.ws.readyState !== WebSocket.OPEN) {
              console.warn('[WebSocketClient] WebSocket not open, scheduling reconnect');
              this.setState(ConnectionState.Disconnected);
              this.scheduleReconnect();
            }
          }
        } else {
          // WebSocket is not in OPEN state - connection is dead
          console.warn('[WebSocketClient] WebSocket not in OPEN state during ping, state:', this.ws.readyState);
          this.setState(ConnectionState.Disconnected);
          this.scheduleReconnect();
        }
      }
    }, 20000); // Ping every 20 seconds (safer for 30-second timeouts)
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Set authentication token, address, and device ID
   * Mirrors SetAuthToken from ws_manager.go
   */
  setAuthToken(token: string, address: string, deviceID: string): void {
    this.config.authToken = token;
    this.config.walletAddress = address;
    this.config.deviceID = deviceID;
    // Update original config to reflect explicit values
    this.originalConfig.authToken = token;
    this.originalConfig.walletAddress = address;
    this.originalConfig.deviceID = deviceID;
    console.log(`Auth set: address=${address}, device=${deviceID}`);
  }

  /**
   * Update authentication credentials
   * Kept for backward compatibility
   */
  async updateAuth(authToken?: string, walletAddress?: string, deviceID?: string): Promise<void> {
    if (authToken !== undefined) {
      this.config.authToken = authToken;
      this.originalConfig.authToken = authToken;
    }
    if (walletAddress !== undefined) {
      this.config.walletAddress = walletAddress;
      this.originalConfig.walletAddress = walletAddress;
    }
    if (deviceID !== undefined) {
      this.config.deviceID = deviceID;
      this.originalConfig.deviceID = deviceID;
    }

    // Reconnect if currently connected to apply new auth
    if (this.isConnected()) {
      this.disconnect();
      await this.connect();
    }
  }
  
  /**
   * Wait for subscription acknowledgment
   * 
   * @param channel - Channel name to wait for
   * @param timeoutMs - Timeout in milliseconds (default: 5000)
   * @returns Promise that resolves when subscription is acknowledged
   */
  waitForSubscription(channel: string, timeoutMs: number = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already subscribed (no pending subscription)
      if (!this.pendingSubscriptions.has(channel)) {
        resolve();
        return;
      }
      
      const timeout = window.setTimeout(() => {
        this.pendingSubscriptions.delete(channel);
        reject(new Error(`Subscription acknowledgment timeout for channel: ${channel}`));
      }, timeoutMs);
      
      const pending = this.pendingSubscriptions.get(channel);
      if (pending) {
        pending.timeout = timeout;
        pending.resolve = resolve;
        pending.reject = reject;
      } else {
        clearTimeout(timeout);
        resolve();
      }
    });
  }
  
  /**
   * Register a pending subscription (called before sending subscribe message)
   * 
   * @param channel - Channel name
   */
  registerPendingSubscription(channel: string): void {
    if (!this.pendingSubscriptions.has(channel)) {
      this.pendingSubscriptions.set(channel, {
        resolve: () => {},
        reject: () => {},
        timeout: 0,
      });
    }
  }
  
  /**
   * Check if ready for subscriptions (connected and authenticated if required)
   */
  isReadyForSubscriptions(): boolean {
    const isConnected = this.state === ConnectionState.Connected;
    // Only JWT (authToken) is required for auth-required channels, not EIP-712
    const authRequired = this.config.authToken;
    const authOk = !authRequired || this.authState === 'authenticated';
    
    return isConnected && authOk;
  }
  
  /**
   * Get current authentication state
   */
  getAuthState(): 'pending' | 'authenticated' | 'failed' | 'not_required' {
    return this.authState;
  }
}
